import { LogoutOutlined, MailFilled, SettingOutlined } from '@ant-design/icons'
import { ManyTag } from '@components/_SiteWide/CellTemplates/ManyTag'
import { MarketSelect } from '@components/_SiteWide/HeaderControls'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { Horizontal, Texto, useThemeContext, GraviButton, Vertical } from '@gravitate-js/excalibrr'
import { getNameInitials, setDisplayTimezoneString, setShowNetworkTimezone } from '@utils/general'
import { Avatar, Drawer, Select } from 'antd'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { AlertsDisplay } from './AlertsDisplay'
import { useUserSettings } from '@hooks/settings'

const { Option } = Select
export function ControlPanel({ onClose, visible, setMode, setSelectedAlert, hasAssignedSites, marketData }) {
  return (
    <Drawer
      title={`Control Panel (v.${import.meta.env.PACKAGE_VERSION})`}
      placement='right'
      width={450}
      onClose={onClose}
      visible={visible}
    >
      <Vertical height={'100%'}>
        <UserDetails />
        {!hasAssignedSites && <SiteMarketSelect marketData={marketData} />}
        <ThemeSelect />
        <TimeDisplaySelect />

        {!hasAssignedSites && <AlertsDisplay setMode={setMode} setSelectedAlert={setSelectedAlert} />}
      </Vertical>
    </Drawer>
  )
}
function UserDetails() {
  const { user, handleLogout, getUser } = useSecurityContext()
  const navigate = useNavigate()
  const { resetSettings } = useUserSettings()

  const name = useMemo(() => {
    if (!user)
      getUser().then((res) => {
        return res?.username || res?.full_name
      })
    return user?.full_name || user?.username
  }, [user])
  return (
    <div className='pb-3 px-4'>
      <Horizontal style={{ width: '100%' }} alignItems='center'>
        <Vertical height='auto'>
          <Horizontal gap={8} alignItems={'center'}>
            <Texto style={{ flex: 1 }} category='heading-small' appearance='medium'>
              CURRENT USER
            </Texto>
            <GraviButton
              size='small'
              onClick={resetSettings}
              icon={<SettingOutlined />}
              type='link'
              buttonText='Reset'
              data-cy='reset-settings-btn'
            />
            <GraviButton
              size='small'
              onClick={() => handleLogout(navigate)}
              icon={<LogoutOutlined />}
              type='link'
              buttonText='Logout'
            />
          </Horizontal>
          <div className='flex items-center my-4'>
            <div className=' vertical-flex-center mr-4'>
              <Avatar shape='square' style={{ borderRadius: 5, background: 'var(--primary-gradient)' }} size={60}>
                {name && getNameInitials(name)}
              </Avatar>
            </div>
            <div>
              <Texto weight='bold' category='h5' className='pb-1'>
                {name}
              </Texto>
              {user?.roles && <ManyTag tagItems={user.roles} maxCount={2} />}
              <Texto appearance='secondary' className='flex items-center mt-2'>
                <MailFilled className='pr-3' />
                {user?.email || 'No Email'}
              </Texto>
            </div>
          </div>
        </Vertical>
        <div className='vertical-flex'></div>
      </Horizontal>
      <div className='border-div' />
    </div>
  )
}
function ThemeSelect() {
  const { availableThemes, currentTheme, setTheme } = useThemeContext()
  const { updateSetting } = useUserSettings()

  return (
    <Vertical flex='none' justifyContent='space-around' height={'auto'} className='p-4' style={{ overflow: 'visible' }}>
      <Texto category='heading-small' appearance='medium'>
        SELECTED THEME
      </Texto>
      <Select
        onChange={async (value) => {
          await updateSetting('storageTheme', value)
          setTheme(value)
        }}
        defaultValue={currentTheme}
      >
        {availableThemes.map((theme, i) => (
          <Option key={i} value={theme.key}>
            {theme.display}
          </Option>
        ))}
      </Select>
      <div className='border-div' />
    </Vertical>
  )
}
function SiteMarketSelect({ marketData }) {
  return (
    <Vertical flex='none' justifyContent='space-around' height={'auto'} className='p-4' style={{ overflow: 'visible' }}>
      <Texto category='heading-small' appearance='medium'>
        SELECTED MARKET
      </Texto>
      <MarketSelect reloadOnSectorChange marketData={marketData} />
      <div className='border-div' />
    </Vertical>
  )
}

const TIME_DISPLAY_OPTIONS = [
  { value: 'LOCAL', text: 'Local Time (Default)' },
  { value: 'NETWORK', text: 'Location Time (Site, Terminal)' },
  { value: 'NETWORK_WITH_TIMEZONE', text: 'Location Time (Site, Terminal) with Timezone Display' },
]

function updateTimeDisplay(value) {
  const { updateSetting } = useUserSettings()
  switch (value) {
    case 'LOCAL':
      updateSetting('USE_NETWORK_TIMEZONE', false)
      updateSetting('DISPLAY_TIMEZONE_STRING', false)
      setShowNetworkTimezone(false)
      setDisplayTimezoneString(false)
      break
    case 'NETWORK':
      updateSetting('USE_NETWORK_TIMEZONE', true)
      updateSetting('DISPLAY_TIMEZONE_STRING', false)
      setShowNetworkTimezone(true)
      setDisplayTimezoneString(false)
      break
    case 'NETWORK_WITH_TIMEZONE':
      updateSetting('USE_NETWORK_TIMEZONE', true)
      updateSetting('DISPLAY_TIMEZONE_STRING', true)
      setShowNetworkTimezone(true)
      setDisplayTimezoneString(true)
      break
  }
  location.reload()
}

function TimeDisplaySelect() {
  const showNetworkTime = localStorage.getItem('USE_NETWORK_TIMEZONE') === 'true'
  const showTimezoneString = localStorage.getItem('DISPLAY_TIMEZONE_STRING') === 'true'
  const value = showTimezoneString ? 'NETWORK_WITH_TIMEZONE' : showNetworkTime ? 'NETWORK' : 'LOCAL'
  return (
    <Vertical flex='none' justifyContent='space-around' height={'auto'} className='p-4' style={{ overflow: 'visible' }}>
      <Texto category='heading-small' appearance='medium'>
        SELECTED TIME
      </Texto>
      <Select onChange={updateTimeDisplay} showSearch className='market-select' style={{ width: '100%' }} value={value}>
        {TIME_DISPLAY_OPTIONS.map((d, i) => (
          <Option value={d.value} key={i}>
            {d.text}
          </Option>
        ))}
      </Select>
      <div className='border-div' />
    </Vertical>
  )
}
